// ============================================================================
// SYLLABUS — LifeOS School.dc.html, option 6b, the 352px right column.
//
// Every fact in this panel was read out of a document by a model. None of it is synced data, and
// the difference matters: a grading weight that is wrong because the extractor misread a table is
// a different kind of wrong from a grade Canvas got wrong, and the owner can only tell them apart
// if the panel says which it is.
//
// So the whole panel sits behind one amber provenance chip and behind <Provenanced>, which means
// an extract with no model, source or timestamp renders NOTHING rather than rendering as fact.
//
// ── WHAT THE CHIP SAYS, AND WHY IT IS NOT ALWAYS THE HANDOFF'S WORDS ───────────────────────────
//
// The mockup's chip reads "extracted from syllabus PDF · Aug 26", and for the demo dataset — a
// hand-written extract of an actual PDF — that is exactly right. In production it usually is not:
// the extract route reads ONE syllabus field, the HTML a professor typed into Canvas's syllabus
// editor (apps/worker/src/routes/syllabus.ts). So the chip follows `sourceFile`, which records
// what was really read. Naming a PDF nobody opened would be a provenance label that lies about
// provenance, which is worse than no label — and the two invariants above any pixel
// (docs/design/FIDELITY.md) settle it.
//
// ── THE FILE HALF, ADDED 2026-08-28, AND WHY IT IS NOT UNDER THE CHIP ──────────────────────────
//
// This panel used to say "no syllabus has been read" for courses whose syllabus is a PDF sitting
// in the Canvas Files tab — COGSCI 1's is `CogSci1_F26_Syllabus.pdf` — because the only field it
// could see was the one the professor left empty. The connector now catalogs every course file
// and pulls the text layer out of the ones whose name says "syllabus"
// (apps/worker/src/lib/course-files.ts), so `SyllabusFile` below shows the file it found and what
// came out of it.
//
// NO PROVENANCE CHIP ON THAT HALF, deliberately: no model touched it. See `SyllabusFile`.
//
// ── THE EMPTY STATE IS STILL A REAL STATE ──────────────────────────────────────────────────────
//
// A course can genuinely have neither — no typed syllabus page and no file whose name says
// syllabus. The panel then says so in the owner's own terms and offers the one action that could
// change it, rather than implying that a future sync will fix something a sync cannot reach.
// ============================================================================

import type { CSSProperties } from 'react'
import {
  semesterWeek,
  weekdayName,
  type SyllabusDocument,
  type SyllabusExtract,
  type Term,
} from '@app/shared'

import { SkeletonRows } from '../Card'
import { useGenerateSyllabus, useSyllabusDocument } from '../../lib/data'
import { Doc, InfoCircle } from './icons'
import { examCountdown } from './derive'
import { Unavailable } from './Chrome'
import { Eyebrow, Provenanced, ProvenanceLabel } from './Provenance'

/**
 * 'extracted from syllabus PDF' · 'extracted from the Canvas syllabus page'.
 *
 * Read off what was actually read, never off what a syllabus usually is.
 */
function extractedFrom(sourceFile: string): string {
  return /\.pdf$/i.test(sourceFile.trim())
    ? 'extracted from syllabus PDF'
    : `extracted from ${sourceFile}`
}

export function SyllabusPanel({
  syllabus,
  courseId,
  term,
  isPending,
  error,
  today,
}: {
  syllabus: SyllabusExtract | null | undefined
  /** Which course to read. Undefined while the course itself is still loading. */
  courseId: string | undefined
  term: Term | null | undefined
  isPending: boolean
  error: unknown
  today: string
}) {
  const currentWeek = term ? semesterWeek(today, term.instructionStarts) : 0
  const generate = useGenerateSyllabus()
  // The FILE half. Its own read, because it comes from a different table and no model touched it.
  // Its failure is never this card's failure: a document lookup that errors leaves the panel
  // exactly as it was before there was one.
  const { data: syllabusFile } = useSyllabusDocument(courseId)

  return (
    <section className="flex flex-none flex-col gap-[13px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <Doc size={16} strokeWidth={1.7} className="flex-none text-muted" />
        <h2 className="text-[13px] font-semibold leading-none">Syllabus</h2>
      </header>

      {isPending && <SkeletonRows rows={5} />}
      <Unavailable error={error} />

      <Provenanced
        artifact={syllabus}
        fallback={
          !isPending && !error ? (
            // THE FILE COMES FIRST WHEN THERE IS ONE. No model has read this course's syllabus,
            // but the connector found the PDF and pulled its text — that is more than the empty
            // state below can say, and saying less than we know is the failure this card exists
            // to avoid.
            syllabusFile ? (
              <SyllabusFile document={syllabusFile} />
            ) : (
            <div className="flex flex-col items-start gap-[9px]">
              <p className="text-[12px] leading-[1.45] text-microcopy text-pretty">
                No syllabus has been read for this course. Canvas exposes only what an instructor
                types into the syllabus page — an attached PDF or a course website is invisible to
                it, and that is the usual case.
              </p>
              {/* The one action that could change this, and it says what it does. The Worker
                  answers "no syllabus posted in bCourses" when the page is empty, which is the
                  honest end of the road rather than a failure. */}
              <button
                type="button"
                disabled={!courseId || generate.isPending}
                onClick={() => courseId && generate.mutate(courseId)}
                style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
                className="mo-ink rounded-button border border-line-2 bg-surface px-3 py-[7px] text-[11.5px] font-semibold leading-none text-ink-2 disabled:cursor-not-allowed disabled:text-microcopy"
              >
                {generate.isPending ? 'Reading the syllabus…' : 'Read the Canvas syllabus'}
              </button>
              {generate.error && (
                // The WORKER'S OWN sentence, verbatim. "There is no syllabus in bCourses", "the
                // syllabus states no grading weights" and "this deployment has no
                // ANTHROPIC_API_KEY" are three different next moves for the owner, and a house
                // error message would collapse them into one dead end.
                <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
                  {generate.error instanceof Error
                    ? generate.error.message
                    : 'The syllabus could not be read.'}
                </p>
              )}
            </div>
            )
          ) : null
        }
      >
        {(extract) => (
          <>
            <span className="flex items-center gap-[6px] self-start rounded-[8px] bg-warning-fill px-[9px] py-[5px]">
              <InfoCircle size={11} className="flex-none text-warning-2" />
              <ProvenanceLabel
                what={extractedFrom(extract.sourceFile)}
                artifact={extract}
                className="text-[10.5px] font-medium leading-none text-warning-2"
              />
            </span>

            <div className="flex flex-col gap-2">
              <Eyebrow>GRADING BREAKDOWN</Eyebrow>
              {extract.gradingBreakdown.map((component) => (
                <div key={component.label} className="flex items-center gap-[10px]">
                  <span className="min-w-0 flex-1 truncate text-[11.5px] font-normal leading-none text-ink-2">
                    {component.label}
                  </span>
                  {/* The track is drawn against a 50% ceiling, which is the mockup's own scale —
                      a 25% component fills half of it. Nothing here is a percentage of a
                      percentage; the number beside it is the fact. */}
                  <span className="h-[5px] w-16 flex-none overflow-hidden rounded-[3px] bg-fill">
                    <span
                      className="block h-full rounded-[3px] bg-school"
                      style={{ width: `${Math.min(100, component.weightPct * 2)}%` }}
                    />
                  </span>
                  <span className="w-[30px] flex-none text-right text-[11.5px] font-semibold leading-none text-ink">
                    {component.weightPct}%
                  </span>
                </div>
              ))}
            </div>

            {extract.exams.length > 0 && (
              <>
                <span className="h-px bg-fill" aria-hidden />
                <div className="flex flex-col gap-2">
                  <Eyebrow>EXAM DATES</Eyebrow>
                  {extract.exams.map((exam, i) => (
                    <div
                      key={exam.label}
                      className={[
                        'flex items-center gap-[9px] rounded-button border-l-[3px] bg-surface-2 px-[10px] py-[7px]',
                        // Only the NEXT exam wears the course hue. The one in December is real
                        // but it is not a thing to act on today.
                        i === 0 ? 'border-school' : 'border-faintest-2',
                      ].join(' ')}
                    >
                      <span className="flex-none text-[11.5px] font-semibold leading-none">
                        {exam.label}
                      </span>
                      <span className="text-[11px] font-normal leading-none text-muted">
                        {weekdayName(exam.date, true)} {shortDate(exam.date)} · {exam.detail}
                      </span>
                      <span className="ml-auto flex-none text-[11px] font-semibold leading-none text-muted">
                        {examCountdown(exam.date, today)}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {extract.weeklyTopics.length > 0 && (
              <>
                <span className="h-px bg-fill" aria-hidden />
                <div className="flex flex-col gap-[7px]">
                  <Eyebrow>WEEKLY TOPICS</Eyebrow>
                  {extract.weeklyTopics.map((topic) => {
                    const past = currentWeek > 0 && topic.week < currentWeek
                    const now = currentWeek > 0 && topic.week === currentWeek
                    return (
                      <div key={topic.week} className="flex items-center gap-[10px]">
                        <span
                          className={[
                            'w-6 flex-none text-[10px] font-semibold leading-none',
                            now ? 'text-info' : 'text-faintest-2',
                          ].join(' ')}
                        >
                          W{topic.week}
                        </span>
                        <span
                          className={[
                            'min-w-0 flex-1 text-[11.5px] leading-[1.3]',
                            now
                              ? 'font-semibold text-ink'
                              : past
                                ? 'font-normal text-microcopy'
                                : 'font-normal text-ink-2',
                          ].join(' ')}
                        >
                          {topic.title}
                        </span>
                        {now && (
                          <span className="flex-none rounded-pill bg-info-fill px-[9px] py-1 text-[10.5px] font-semibold leading-none text-info">
                            this week
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}
      </Provenanced>
    </section>
  )
}

/**
 * THE SYLLABUS PDF THE CONNECTOR FOUND, and what came out of it.
 *
 * ── WHY THIS IS NOT UNDER A PROVENANCE CHIP ────────────────────────────────────────────────────
 *
 * Everything else on this panel was written by a model and wears an amber chip saying so. This
 * was not. The Canvas Files API listed a PDF, the Worker pulled out its text layer, and the text
 * below is the professor's own words in the professor's own order — a transcription, not a
 * reading. Putting a provenance chip on it would tell the owner a model was involved when none
 * was, which is the same lie as omitting one where there was.
 *
 * ── THREE OUTCOMES, THREE SENTENCES ────────────────────────────────────────────────────────────
 *
 * A file that WAS read shows its text. A file that could not be read says so — "found, could not
 * be read" — and prints the Worker's own reason, because "it is a scan with no text layer" and
 * "it is 90 pages, past what LifeOS reads" are two different next moves for the owner. A file
 * that was never read (a .docx, say) says only that it is there. What none of the three does is
 * go quiet: the owner can see the file in bCourses, and a card that knows about it and says
 * nothing is a card he stops believing.
 */
function SyllabusFile({ document: doc }: { document: SyllabusDocument }) {
  const { file, text } = doc
  return (
    <div className="flex flex-col items-start gap-[9px]">
      <p className="text-[12px] font-semibold leading-[1.35] text-ink text-pretty">
        Syllabus PDF found: {file.displayName}
      </p>

      {text !== null ? (
        <>
          <p className="text-[10.5px] font-medium leading-none text-microcopy">
            {extractionMeta(file.pages, file.characters)}
          </p>
          {/* The text is scrolled rather than truncated: a syllabus is a document, and a card
              that shows the first paragraph and hides the grading table is a card that decided
              which half mattered. */}
          <div className="max-h-[260px] w-full overflow-y-auto rounded-button bg-surface-2 px-[10px] py-[9px]">
            <p className="whitespace-pre-wrap text-[11.5px] leading-[1.45] text-ink-2">{text}</p>
          </div>
        </>
      ) : (
        <div className="flex flex-col items-start gap-[5px]">
          <p className="text-[11.5px] font-semibold leading-none text-warning-2">
            {file.extraction === 'not_attempted' ? 'Not read' : 'Found, could not be read'}
          </p>
          <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
            {file.extractionError ??
              'Nothing has tried to read this file — LifeOS reads PDFs, and this one is not.'}
          </p>
        </div>
      )}

      {/* Canvas's own link, so the owner can open the file itself. It carries a verifier that
          expires; when it does, bCourses asks him to sign in, which is the honest failure. */}
      {file.url && (
        <a
          href={file.url}
          target="_blank"
          rel="noreferrer"
          style={{ '--mo-rule': 'var(--color-school)' } as CSSProperties}
          className="mo-ink rounded-button border border-line-2 bg-surface px-3 py-[7px] text-[11.5px] font-semibold leading-none text-ink-2"
        >
          Open in bCourses
        </a>
      )}
    </div>
  )
}

/** '4 pages · 612 characters' — only the halves that are actually known. */
function extractionMeta(pages: number | null, characters: number | null): string {
  const parts: string[] = []
  if (pages !== null) parts.push(`${pages} page${pages === 1 ? '' : 's'}`)
  if (characters !== null) parts.push(`${characters.toLocaleString('en-US')} characters`)
  return parts.join(' · ')
}

/** 'Oct 8' without repeating the weekday the caller already printed. */
function shortDate(date: string): string {
  const [, month, day] = date.split('-')
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${MONTHS[Number(month) - 1]} ${Number(day)}`
}
